import { api } from '@/utils/api';

export const useTeamMemberInviteMutation = () => {
  const utils = api.useUtils();

  return api.team.memberInvite.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        // refetch invited users
        utils.team.invitedTeamUsers.invalidate({ teamId }),
        // a user whose email is in out system is immediately
        // added to the team on invite, hence meed to invalidate teamUsers
        utils.team.teamUsers.invalidate({ teamId }),
        // need to invalidate to reload team member counts
        utils.team.id.invalidate({ teamId }),

        utils.team.all.invalidate(),
        utils.team.membersBootstrap.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamMemberDeleteMutation = () => {
  const utils = api.useUtils();

  return api.team.memberDelete.useMutation({
    onSuccess: async (_, { teamId }) => {
      // refetch team users
      void utils.team.teamUsers.invalidate({ teamId });
      // need to invalidate to reload team member counts
      void utils.team.id.invalidate({ teamId });

      void utils.team.all.invalidate();
      void utils.team.membersBootstrap.invalidate({ teamId });
      await utils.user.me.invalidate();
    },
  });
};

export const useTeamMemberDeleteInvitationMutation = () => {
  const utils = api.useUtils();

  return api.team.inviteDelete.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        // refetch invited users
        utils.team.invitedTeamUsers.invalidate({ teamId }),
        // need to invalidate to reload team member counts
        utils.team.id.invalidate({ teamId }),

        utils.team.all.invalidate(),
        utils.team.membersBootstrap.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateMutation = () => {
  const utils = api.useUtils();
  return api.team.update.useMutation({
    onSuccess: async ({ teamId, userId }) => {
      void utils.team.all.invalidate();
      void utils.user.me.invalidate();

      await Promise.all([
        utils.team.teamUser.invalidate({ teamId, userId }),
        utils.team.id.invalidate({ teamId }),
        utils.team.idWithAll.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateImageMutation = () => {
  const utils = api.useUtils();
  return api.team.updateImage.useMutation({
    onSuccess: async (res) => {
      const teamId = res.teamId;
      const userId = res.userId;

      utils.user.me.setData(undefined, (data) => {
        if (data && data.userTeam && data.userTeam.teamId == teamId) {
          return {
            ...data,
            userTeam: {
              ...data.userTeam,
              teamLogo: res.teamLogo,
            },
          };
        }
        return data;
      });
      utils.team.teamUser.setData({ teamId, userId }, (data) => {
        if (data) {
          return {
            ...data,
            teamLogo: res.teamLogo,
          };
        }
        return data;
      });
      utils.team.id.setData({ teamId }, (data) => {
        if (data) {
          return {
            ...data,
            teamLogo: res.teamLogo,
          };
        }
        return data;
      });

      await utils.team.all.invalidate();
    },
  });
};

export const useTeamCreateWebhookMutation = () => {
  const utils = api.useUtils();
  return api.team.webhookCreate.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.webhook.invalidate({ teamId }),
        utils.team.integrationsBootstrap.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateGTMMutation = () => {
  const utils = api.useUtils();
  return api.team.updateGtm.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.gtm.invalidate({ teamId }),
        utils.team.integrationsBootstrap.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamRemoveSlackMutation = () => {
  const utils = api.useUtils();
  return api.team.removeSlack.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.slack.invalidate({ teamId }),
        utils.team.integrationsBootstrap.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamDeleteMutation = () => {
  const utils = api.useUtils();
  return api.team.delete2.useMutation({
    onSuccess: async (res, { teamId }) => {
      // @TODO: fix
      //
      // if (res?.newDefaultTeamId) {
      //   utils.user.me.setData(undefined, (data) => {
      //     if (data?.user != null) {
      //       return {
      //         user: { ...data.user, defaultTeamId: res.newDefaultTeamId },
      //       };
      //     }
      //     return data;
      //   });
      // }

      //this causes a full page spinner, figure out how to fix it
      await Promise.all([
        utils.team.all.invalidate(),
        utils.user.me.invalidate(),
        utils.team.id.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamCreateMutation = () => {
  const utils = api.useUtils();
  return api.team.create.useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (res, { makeDefault }) => {
      void utils.team.all.invalidate();
      // @TODO: fix
      // if (makeDefault) {
      //   const defaultTeamId = res.teamId;
      //   utils.user.me.setData(undefined, (data) => {
      //     if (data?.user != null) {
      //       return {
      //         user: { ...data.user, defaultTeamId },
      //       };
      //     }
      //     return data;
      //   });
      // }
      await utils.user.me.invalidate();
    },
  });
};

export const useTeamBulkSetPremiumMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.bulkSetPremium.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
    },
  });
};

/** POS-2504: superadmin creates an empty team; the list refetches so the new row appears. */
export const useSuperadminCreateTeamMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.createTeam.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
    },
  });
};

/** POS-2507: add an existing user or create a new one on a team from Super Admin. */
export const useSuperadminAddUserToTeamMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.addUserToTeam.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.superAdmin.teamId.invalidate(),
        utils.superAdmin.allTeams.invalidate(),
        utils.superAdmin.allUsers.invalidate(),
        utils.superAdmin.userTeams.invalidate(),
      ]);
    },
  });
};

/** POS-2508: send (or resend) the welcome email; refetch so the badges + section flip to "sent". */
export const useSuperadminSendWelcomeEmailMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.sendWelcomeEmail.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.superAdmin.userId.invalidate(),
        utils.superAdmin.teamId.invalidate(),
        utils.superAdmin.allUsers.invalidate(),
      ]);
    },
  });
};

export const useTeamUpdateUsersCountMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.updateUsersCount.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
    },
  });
};

export const useTeamUpdateUser = () => {
  const utils = api.useUtils();
  const invalidateAll = async () => {
    await Promise.all([
      utils.superAdmin.allTeams.invalidate(),
      utils.superAdmin.teamId.invalidate(),
      utils.superAdmin.allUsers.invalidate(),
      utils.superAdmin.userId.invalidate(),
      utils.superAdmin.userTeams.invalidate(),
    ]);
  };
  return api.superAdmin.updateUser.useMutation({
    onSuccess: invalidateAll,
    // Roll back any optimistic cache patches by re-fetching from the server.
    onError: invalidateAll,
  });
};

export const useTeamUpdateTeamDataMutation = () => {
  const utils = api.useUtils();
  const invalidateAll = async (_: unknown, { teamId }: { teamId: string }) => {
    await Promise.all([
      utils.superAdmin.allTeams.invalidate(),
      utils.superAdmin.teamId.invalidate(),
      utils.superAdmin.userTeams.invalidate(),
      utils.superAdmin.userId.invalidate(),
      utils.team.id.invalidate({ teamId }),
    ]);
  };
  return api.superAdmin.updateTeamData.useMutation({
    onSuccess: invalidateAll,
    // Roll back any optimistic cache patches by re-fetching from the server.
    onError: invalidateAll,
  });
};

export const useTeamUpdateOnboardingMutation = () => {
  const utils = api.useUtils();
  return api.team.updateOnboarding.useMutation({
    onSuccess: async () => {
      await utils.team.onboarding.invalidate();
    },
  });
};

export const useTeamUpdateVoiceProviderMutation = () => {
  const utils = api.useUtils();
  return api.team.updateVoiceProvider.useMutation({
    onSuccess: async () => {
      // Refresh the settings view AND the cached userTeam, so the next click-to-call
      // builds the softphone client for the newly-selected carrier.
      await Promise.all([utils.team.idWithAll.invalidate(), utils.user.me.invalidate()]);
    },
  });
};
